import defaults from '../core/feel.json';
import { createRig } from './rig.js';

/**
 * A person.
 *
 * This used to be the capsule — the collision shape, drawn, so that what you
 * saw and what you hit were the same object. **The capsule is still the
 * collision shape**, at the same radius and the same height; it is simply no
 * longer what is drawn over it. Every distance in this project is still
 * measured against it and not one of them knows a figure exists.
 *
 * What is left here is the interface the rest of the game already used — an
 * object3D, a place to put it, and the two colours — so that putting a body on
 * the bones changed no call site. The rig is `rig.js`; what a body is made of
 * is `figure.js`, which is the one file a real mesh replaces.
 */
export function createAvatar({
  color = 0x3c4452,
  accent = 0xe8e4dc,
  radius = defaults.radius,
  height = defaults.height,
} = {}) {
  const rig = createRig({ height, radius, colour: color, accent });

  return {
    object3D: rig.object3D,
    /** The collision shape, restated, so a check can prove it has not moved. */
    capsule: rig.capsule,
    /** Hip to ankle. What a stride is measured against, and what a step lands on. */
    legLength: rig.legLength,

    /** Places the avatar at a body's feet position and facing. */
    sync(position, yaw) {
      rig.sync(position, yaw);
    },

    /**
     * One frame of animation, from a reading of the body.
     *
     * Read-only: a plain object of numbers in, bones out, and no path from here
     * to anything the game measures.
     */
    pose(body, dt) {
      return rig.pose(body, dt);
    },

    /** What it is playing, for a headless check. */
    playing: () => rig.playing(),
    reset: () => rig.reset(),

    setColor(next) {
      rig.setColor(next);
    },
    /**
     * The shirt, which doubles as the facing cue.
     *
     * Separate from the body colour because the two answer different questions:
     * the body says what you are wearing, the shirt says which way you are
     * pointed. A clothing change moves the first and must never move the second
     * far enough to stop reading.
     */
    setAccent(next) {
      rig.setAccent(next);
    },
    dispose() {
      rig.dispose();
    },
  };
}
